import { useEffect, useMemo, useState } from "react";
import { Overheads } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/bill-utils";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Briefcase,
  Plus,
  Trash2,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  Home,
  Users,
  Shield,
  Repeat,
  Zap,
  Truck,
  CreditCard,
  MoreHorizontal,
  Pencil,
  Save,
  X,
  CalendarClock,
} from "lucide-react";

const CAT_META = {
  Rent: { icon: Home, color: "bg-red-50 text-red-800 border-red-200" },
  Wages: { icon: Users, color: "bg-blue-50 text-blue-800 border-blue-200" },
  Super: { icon: Shield, color: "bg-green-50 text-green-800 border-green-200" },
  Insurance: { icon: Shield, color: "bg-purple-50 text-purple-800 border-purple-200" },
  Subscription: { icon: Repeat, color: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  Utilities: { icon: Zap, color: "bg-amber-50 text-amber-800 border-amber-200" },
  Vehicle: { icon: Truck, color: "bg-neutral-100 text-neutral-800 border-neutral-300" },
  "Loan/Finance": { icon: CreditCard, color: "bg-rose-50 text-rose-800 border-rose-200" },
  Other: { icon: MoreHorizontal, color: "bg-neutral-100 text-neutral-700 border-neutral-300" },
};

const FREQ_LABEL = {
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

const DOW = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DOW_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
const dueLabel = (o) => {
  if (!o) return "";
  const wk = ["weekly", "fortnightly"].includes(o.frequency);
  if (wk && o.due_day_of_week != null) return `Every ${DOW[o.due_day_of_week]}`;
  if (!wk && o.due_day_of_month != null) return `Every ${ordinal(o.due_day_of_month)}`;
  return "";
};

const OverheadsPage = () => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ categories: [], frequencies: [] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null); // null | 'new' | overhead.id
  const [expandedId, setExpandedId] = useState(null);
  const emptyForm = {
    name: "",
    category: "Rent",
    amount: "",
    frequency: "weekly",
    due_day_of_week: 0,
    due_day_of_month: 1,
    account_paid_from: "",
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [payAmount, setPayAmount] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([Overheads.list(), Overheads.summary()]);
      setItems(l.overheads);
      setMeta({ categories: l.categories, frequencies: l.frequencies });
      setSummary(s);
    } catch {
      toast.error("Could not load overheads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditingId("new");
    setShowAdd(true);
  };

  const openEdit = (o) => {
    setForm({
      name: o.name || "",
      category: o.category || "Other",
      amount: String(o.amount ?? ""),
      frequency: o.frequency || "weekly",
      due_day_of_week: o.due_day_of_week ?? 0,
      due_day_of_month: o.due_day_of_month ?? 1,
      account_paid_from: o.account_paid_from || "",
      notes: o.notes || "",
    });
    setEditingId(o.id);
    setShowAdd(true);
    // Scroll form into view
    setTimeout(() => {
      document
        .querySelector('[data-testid="overhead-add-form"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const closeForm = () => {
    setShowAdd(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const saveOverhead = async (e) => {
    e && e.preventDefault();
    if (!form.name.trim() || !(Number(form.amount) > 0)) {
      toast.error("Name and amount required");
      return;
    }
    const isWeeklyish = ["weekly", "fortnightly"].includes(form.frequency);
    const payload = {
      name: form.name.trim(),
      category: form.category,
      amount: Number(form.amount),
      frequency: form.frequency,
      account_paid_from: form.account_paid_from.trim(),
      notes: form.notes.trim(),
      due_day_of_week: isWeeklyish ? Number(form.due_day_of_week) : null,
      due_day_of_month: !isWeeklyish ? Number(form.due_day_of_month) : null,
    };
    try {
      if (editingId && editingId !== "new") {
        await Overheads.update(editingId, payload);
        toast.success("Updated");
      } else {
        await Overheads.create(payload);
        toast.success(`${form.name} added`);
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const removeOverhead = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? All payment history will be lost.`))
      return;
    try {
      await Overheads.remove(id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const recordPayment = async (id, defaultAmount) => {
    const amt = Number(payAmount[id] || defaultAmount || 0);
    if (!(amt > 0)) {
      toast.error("Enter an amount > 0");
      return;
    }
    try {
      await Overheads.addPayment(id, { amount: amt });
      toast.success(`${fmtMoney(amt)} recorded`);
      setPayAmount({ ...payAmount, [id]: "" });
      load();
    } catch {
      toast.error("Could not record payment");
    }
  };

  const deletePayment = async (id, paymentId) => {
    if (!window.confirm("Remove this payment?")) return;
    try {
      await Overheads.deletePayment(id, paymentId);
      toast.success("Payment removed");
      load();
    } catch {
      toast.error("Could not remove payment");
    }
  };

  const grouped = useMemo(() => {
    const out = {};
    for (const it of items) {
      const c = it.category || "Other";
      out[c] = out[c] || [];
      out[c].push(it);
    }
    return out;
  }, [items]);

  if (loading) {
    return <div className="text-sm text-neutral-500 p-4">Loading overheads…</div>;
  }

  return (
    <div data-testid="overheads-page" className="fade-rise">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="swiss-label mb-3 flex items-center gap-2">
            <Briefcase className="h-3.5 w-3.5" /> Overheads
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
            Fixed weekly outgoings.
          </h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Rent, wages, super, insurance, subscriptions — the things you pay
            like clockwork every week/month, kept separate from client bills so
            your Bills tab stays clean.
          </p>
        </div>
        <button
          data-testid="overhead-add-btn"
          onClick={showAdd ? closeForm : openNew}
          className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {showAdd ? "Close" : "Add overhead"}
        </button>
      </div>

      {/* KPI tiles */}
      {summary && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="swiss-card">
            <div className="swiss-label">Weekly target</div>
            <div className="mt-2 font-display font-black text-2xl tabular-nums">
              {fmtMoney(summary.weekly_target)}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500">
              across {summary.overheads.length} item
              {summary.overheads.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="swiss-card">
            <div className="swiss-label">Paid this week</div>
            <div className="mt-2 font-display font-black text-2xl tabular-nums text-green-700">
              {fmtMoney(summary.paid_this_week)}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500">
              {fmtDate(summary.week_start)} — {fmtDate(summary.week_end)}
            </div>
          </div>
          <div className="swiss-card">
            <div className="swiss-label">Still to pay</div>
            <div
              className={`mt-2 font-display font-black text-2xl tabular-nums ${summary.remaining_this_week > 0 ? "text-amber-700" : "text-green-700"}`}
            >
              {fmtMoney(summary.remaining_this_week)}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500">
              {summary.remaining_this_week > 0 ? "this week" : "all clear"}
            </div>
          </div>
          <div className="swiss-card">
            <div className="swiss-label">Annualised</div>
            <div className="mt-2 font-display font-black text-2xl tabular-nums">
              {fmtMoney(summary.weekly_target * 52)}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-widest text-neutral-500">
              per year
            </div>
          </div>
        </section>
      )}

      {/* Add / Edit form */}
      {showAdd && (
        <form
          onSubmit={saveOverhead}
          className="swiss-card mb-6 space-y-4 border-l-4 border-l-black"
          data-testid="overhead-add-form"
        >
          <div className="flex items-center justify-between">
            <div className="font-display font-extrabold text-lg">
              {editingId && editingId !== "new" ? "Edit overhead" : "New overhead"}
            </div>
            <button
              type="button"
              onClick={closeForm}
              className="text-neutral-400 hover:text-black"
              aria-label="Close"
              data-testid="oh-cancel-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="swiss-label mb-1">Name *</div>
              <Input
                data-testid="oh-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. 21 Eugine Terrace"
                className="swiss-input"
                required
              />
            </div>
            <div>
              <div className="swiss-label mb-1">Category</div>
              <select
                data-testid="oh-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="swiss-input h-10"
              >
                {meta.categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="swiss-label mb-1">Amount *</div>
              <Input
                data-testid="oh-amount"
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="450.00"
                className="swiss-input mono"
                required
              />
            </div>
            <div>
              <div className="swiss-label mb-1">Frequency</div>
              <select
                data-testid="oh-freq"
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                className="swiss-input h-10"
              >
                {meta.frequencies.map((f) => (
                  <option key={f} value={f}>
                    {FREQ_LABEL[f] || f}
                  </option>
                ))}
              </select>
            </div>
            {/* Due day — switches based on frequency */}
            {["weekly", "fortnightly"].includes(form.frequency) ? (
              <div>
                <div className="swiss-label mb-1">Due day of week</div>
                <select
                  data-testid="oh-due-dow"
                  value={form.due_day_of_week}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      due_day_of_week: Number(e.target.value),
                    })
                  }
                  className="swiss-input h-10"
                >
                  {DOW.map((d, i) => (
                    <option key={i} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <div className="swiss-label mb-1">Due day of month</div>
                <Input
                  data-testid="oh-due-dom"
                  type="number"
                  min="1"
                  max="31"
                  value={form.due_day_of_month}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      due_day_of_month: Number(e.target.value),
                    })
                  }
                  className="swiss-input mono"
                />
              </div>
            )}
            <div>
              <div className="swiss-label mb-1">Account paid from</div>
              <Input
                data-testid="oh-account"
                value={form.account_paid_from}
                onChange={(e) =>
                  setForm({ ...form, account_paid_from: e.target.value })
                }
                placeholder="e.g. Outgoing account"
                className="swiss-input"
              />
            </div>
            <div className="md:col-span-2">
              <div className="swiss-label mb-1">Notes</div>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional"
                className="swiss-input"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              data-testid="oh-save-btn"
              type="submit"
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[11px] px-5 py-2 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
            >
              <Save className="h-3.5 w-3.5" />
              {editingId && editingId !== "new" ? "Save changes" : "Save"}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="border-2 border-neutral-300 hover:border-black font-bold uppercase tracking-widest text-[11px] px-5 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Overheads grouped by category */}
      {items.length === 0 ? (
        <div className="swiss-card text-center py-12" data-testid="overheads-empty">
          <div className="font-display font-extrabold text-xl">
            No overheads yet
          </div>
          <div className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">
            Add your first weekly fixed cost — rent, wages, super, insurance —
            and Billy will track it separately from client bills.
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, list]) => {
            const CatIcon = (CAT_META[cat] || CAT_META.Other).icon;
            const catColor = (CAT_META[cat] || CAT_META.Other).color;
            const catTotal = list.reduce(
              (s, o) => s + Number(o.weekly_amount || 0),
              0,
            );
            return (
              <section key={cat} data-testid={`overheads-group-${cat.toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-2">
                  <span
                    className={`inline-flex items-center gap-1.5 border-2 px-2 py-1 text-[10px] uppercase tracking-widest font-bold ${catColor}`}
                  >
                    <CatIcon className="h-3 w-3" /> {cat}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                    {list.length} item{list.length === 1 ? "" : "s"} ·{" "}
                    <span className="mono">{fmtMoney(catTotal)}/wk</span>
                  </span>
                </div>
                <ul className="space-y-3">
                  {list.map((o) => {
                    const paidThisWeek =
                      summary?.overheads?.find((x) => x.id === o.id)
                        ?.paid_this_week || 0;
                    const remaining =
                      summary?.overheads?.find((x) => x.id === o.id)
                        ?.remaining_this_week || 0;
                    const expanded = expandedId === o.id;
                    return (
                      <li
                        key={o.id}
                        className={`swiss-card ${remaining <= 0 ? "border-l-4 border-l-green-600" : ""}`}
                        data-testid={`overhead-${o.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              {remaining <= 0 && (
                                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                              )}
                              <div className="font-display font-extrabold text-lg truncate">
                                {o.name}
                              </div>
                            </div>
                            <div className="mt-1 text-xs text-neutral-500 flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="mono font-bold">
                                {fmtMoney(o.amount)}
                              </span>{" "}
                              {FREQ_LABEL[o.frequency]?.toLowerCase() ||
                                o.frequency}
                              {o.weekly_amount !== o.amount && (
                                <>
                                  {" "}
                                  ·{" "}
                                  <span className="mono">
                                    {fmtMoney(o.weekly_amount)}/wk
                                  </span>
                                </>
                              )}
                              {dueLabel(o) && (
                                <span className="inline-flex items-center gap-1 border border-neutral-300 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold text-neutral-700 bg-white">
                                  <CalendarClock className="h-3 w-3" />
                                  {dueLabel(o)}
                                </span>
                              )}
                              {o.account_paid_from && (
                                <> · from {o.account_paid_from}</>
                              )}
                            </div>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <div className="font-display font-extrabold text-lg mono tabular-nums">
                              {fmtMoney(paidThisWeek)}
                              <span className="text-xs text-neutral-400 font-normal">
                                {" / "}
                                {fmtMoney(o.weekly_amount)}
                              </span>
                            </div>
                            <div
                              className={`text-[10px] uppercase tracking-widest font-bold ${remaining <= 0 ? "text-green-700" : "text-amber-700"}`}
                            >
                              {remaining <= 0
                                ? "paid this week"
                                : `${fmtMoney(remaining)} to go`}
                            </div>
                          </div>
                        </div>

                        {/* Quick action row */}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <button
                            data-testid={`oh-pay-full-${o.id}`}
                            onClick={() => recordPayment(o.id, o.weekly_amount)}
                            disabled={remaining <= 0}
                            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-1 disabled:opacity-40"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Paid{" "}
                            {fmtMoney(o.weekly_amount)}
                          </button>
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">
                              or
                            </span>
                            <Input
                              data-testid={`oh-pay-amount-${o.id}`}
                              type="number"
                              step="0.01"
                              value={payAmount[o.id] || ""}
                              onChange={(e) =>
                                setPayAmount({
                                  ...payAmount,
                                  [o.id]: e.target.value,
                                })
                              }
                              placeholder="0.00"
                              className="swiss-input mono text-right w-24 h-8"
                            />
                            <button
                              data-testid={`oh-pay-partial-${o.id}`}
                              onClick={() => recordPayment(o.id)}
                              className="border-2 border-neutral-300 hover:border-black font-bold uppercase tracking-widest text-[10px] px-3 py-1.5"
                            >
                              Record
                            </button>
                          </div>
                          <button
                            data-testid={`oh-history-toggle-${o.id}`}
                            onClick={() =>
                              setExpandedId(expanded ? null : o.id)
                            }
                            className="ml-auto text-[10px] uppercase tracking-widest text-neutral-500 hover:text-black font-bold inline-flex items-center gap-1"
                          >
                            {expanded ? (
                              <>
                                Hide history <ChevronDown className="h-3 w-3" />
                              </>
                            ) : (
                              <>
                                {(o.payments || []).length} payment
                                {(o.payments || []).length === 1 ? "" : "s"}{" "}
                                <ChevronRight className="h-3 w-3" />
                              </>
                            )}
                          </button>
                          <button
                            data-testid={`oh-edit-${o.id}`}
                            onClick={() => openEdit(o)}
                            className="text-neutral-400 hover:text-black"
                            aria-label="Edit overhead"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`oh-delete-${o.id}`}
                            onClick={() => removeOverhead(o.id, o.name)}
                            className="text-neutral-400 hover:text-red-600"
                            aria-label="Delete overhead"
                            title="Delete overhead"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Payment history */}
                        {expanded && (
                          <div className="mt-3 pt-3 border-t border-neutral-200">
                            {(o.payments || []).length === 0 ? (
                              <div className="text-xs text-neutral-500">
                                No payments recorded yet.
                              </div>
                            ) : (
                              <ul className="divide-y divide-neutral-100">
                                {[...(o.payments || [])]
                                  .sort((a, b) =>
                                    (b.paid_date || "").localeCompare(
                                      a.paid_date || "",
                                    ),
                                  )
                                  .map((p) => (
                                    <li
                                      key={p.id}
                                      className="py-2 flex items-center justify-between text-sm"
                                    >
                                      <div className="min-w-0">
                                        <span className="mono font-bold">
                                          {fmtMoney(p.amount)}
                                        </span>{" "}
                                        <span className="text-neutral-500">
                                          on {fmtDate(p.paid_date)}
                                        </span>
                                        {p.notes && (
                                          <span className="text-xs text-neutral-500 ml-2 truncate">
                                            · {p.notes}
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        onClick={() =>
                                          deletePayment(o.id, p.id)
                                        }
                                        className="text-neutral-400 hover:text-red-600"
                                        aria-label="Delete payment"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OverheadsPage;
